/**
 * Get USDC balance of a market contract
 */
import type { Address } from "viem"
import { erc20Abi } from "viem"
import { readContract } from "viem/actions"
import { type ReadableClient, readMarketUsdc } from "../../internal/contracts"
import { fromUsdcScale } from "../../internal/convert"

export interface GetMarketBalanceParams {
	market: Address
}

export interface MarketBalance {
	/** USDC balance in human-readable format */
	usdc: number
	/** Raw USDC balance (6 decimals) */
	usdcRaw: bigint
}

/**
 * Get the USDC balance held by a market contract
 *
 * Useful for monitoring market health and reserve levels.
 *
 * @example
 * ```ts
 * import { getMarketBalance } from "@breakout/sdk/actions/read"
 *
 * const balance = await getMarketBalance(publicClient, { market: "0x..." })
 * console.log(`Market holds $${balance.usdc.toFixed(2)} USDC`)
 * ```
 */
export async function getMarketBalance(
	client: ReadableClient,
	params: GetMarketBalanceParams,
): Promise<MarketBalance> {
	// Get the USDC address from the market
	const usdcAddress = await readMarketUsdc(client, params.market)

	// Read the balance
	const balance = await readContract(client, {
		address: usdcAddress,
		abi: erc20Abi,
		functionName: "balanceOf",
		args: [params.market],
	})

	return {
		usdc: fromUsdcScale(balance),
		usdcRaw: balance,
	}
}
